/**
 * Geschäftspartner — die Liste (AP-31, Phase E).
 *
 * **Befund 2.2 (e) erledigt:** Bis hierher war das eine handgebaute Tabelle mit
 * drei fest verdrahteten Filtern und sechs festen Spalten. Der Listen-Standard
 * (`docs/pattern-auswertungsmaske.md`) ist für **jede** Listenseite verbindlich
 * — und ab AP-31 ist er hier nicht mehr Schönheit, sondern Funktion: Die
 * Feldsicht setzt genau an den Spalten- und Filterdefinitionen an, die diese
 * Seite nicht hatte.
 *
 * Kein `dateFrom`/`dateTo` in der Vorbelegung: **Ein Partner ist kein
 * Ereignis.**
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Settings, Check, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { ohneFehlendeRechte } from '@/components/list/list.types'
import type { FilterFieldDef, ActiveFilter, ColumnFieldDef } from '@/components/list/list.types'
import { pageWide } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { adressAblageflaeche, useAdressAblage } from '@/lib/adress-ablage'
import { clientsApi } from '../api/clients.api'
import { CreateClientDialog } from '../components/CreateClientDialog'
import type { Client, DuplicateCandidate, ListClientsParams } from '../types/client.types'

const COLUMN_LAYOUT_KEY = 'columnbar_layout_clients'

/** Ohne Datum: Stammdaten, kein Ereignis (AP-31 8.1). */
const DEFAULT_FILTER_FIELD_KEYS = ['search', 'fulltext', 'role', 'isActive']

const DEFAULT_COLUMN_KEYS = ['clientNumber', 'name', 'role', 'type', 'city', 'projects', 'isActive']

/** Was der Server sortieren kann (`SORTIERBAR` in der ListClientsDto). */
const SERVER_SORTIERBAR = new Set([
  'clientNumber',
  'supplierNumber',
  'name',
  'createdAt',
  'paymentTermsDays',
  'supplierPaymentTermsDays',
])

/**
 * Stand des gespeicherten Spaltenlayouts.
 *
 * **2** (AP-31 Nachtrag): Die Kundennummer ist keine feste erste Spalte mehr,
 * sondern eine wie jede andere — ausblendbar und verschiebbar. Ein Layout aus
 * der Zeit davor kennt sie nicht in `columnKeys`; ohne Hochstufung verlöre jeder
 * Benutzer mit gespeichertem Layout die Spalte beim ersten Öffnen.
 */
const LAYOUT_VERSION = 2

// ─── Spalten und Filter ───────────────────────────────────────────────────────

type Uebersetzer = (key: string) => string

/**
 * **Die Feldrechte stehen an den Spalten, nicht in einer `if`-Zeile daneben.**
 *
 * `ohneFehlendeRechte` streicht damit Anzeige, Spaltenauswahl, Sortierung und
 * Filter in einem Zug (AP-30-F). Und es bleibt Kosmetik: Die eigentliche Sperre
 * ist die Beschneidung der Antwort im Backend.
 */
const buildColumns = (t: Uebersetzer): ColumnFieldDef[] => [
  { key: 'clientNumber', label: t('clients.clientNumber'), category: 'core' },
  { key: 'name', label: t('clients.name'), category: 'core' },
  { key: 'role', label: t('clients.role.label'), category: 'core' },
  { key: 'type', label: t('clients.type'), category: 'core' },
  { key: 'supplierNumber', label: t('clients.supplierNumber'), category: 'core' },
  { key: 'city', label: t('clients.city'), category: 'core' },
  { key: 'email', label: t('clients.email'), category: 'core' },
  { key: 'phone', label: t('clients.phone'), category: 'core' },
  { key: 'projects', label: t('clients.projects'), category: 'core', align: 'right' },
  { key: 'isActive', label: t('common.status'), category: 'core' },
  {
    key: 'vatId',
    label: t('clients.payment.vatId'),
    category: 'core',
    requiresPermission: 'crm.client.payment.view',
  },
  {
    key: 'paymentTermsDays',
    label: t('clients.payment.paymentTermsDays'),
    category: 'core',
    align: 'right',
    requiresPermission: 'crm.client.payment.view',
  },
  {
    key: 'supplierPaymentTermsDays',
    label: t('clients.payment.supplierPaymentTermsDays'),
    category: 'core',
    align: 'right',
    requiresPermission: 'crm.client.payment.view',
  },
  {
    key: 'debitorNumber',
    label: t('clients.accounting.debitorNumber'),
    category: 'core',
    requiresPermission: 'crm.client.accounting.view',
  },
  {
    key: 'kreditorNumber',
    label: t('clients.accounting.kreditorNumber'),
    category: 'core',
    requiresPermission: 'crm.client.accounting.view',
  },
]

const buildFilterFields = (t: Uebersetzer): FilterFieldDef[] => [
  {
    key: 'search',
    label: t('clients.search'),
    type: 'text',
    category: 'core',
    operators: ['contains'],
  },
  {
    // AP-31 Nachtrag: sucht zusätzlich in Adressen und Ansprechpartnern.
    key: 'fulltext',
    label: t('clients.fulltext'),
    type: 'text',
    category: 'core',
    operators: ['contains'],
  },
  {
    key: 'role',
    label: t('clients.role.label'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      { value: 'customer', label: t('clients.role.customer') },
      { value: 'supplier', label: t('clients.role.supplier') },
      { value: 'both', label: t('clients.role.both') },
    ],
  },
  {
    key: 'type',
    label: t('clients.type'),
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      { value: 'COMPANY', label: t('clients.types.COMPANY') },
      { value: 'INDIVIDUAL', label: t('clients.types.INDIVIDUAL') },
    ],
  },
  {
    key: 'country',
    label: t('clients.country'),
    type: 'text',
    category: 'core',
    operators: ['is'],
  },
  {
    key: 'isActive',
    label: t('common.status'),
    type: 'boolean',
    category: 'core',
    operators: ['is'],
  },
]

// ─── Spaltenlayout (AP-23: Cache + Server) ────────────────────────────────────

interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
  version?: number
}

const STANDARD_LAYOUT: ColumnState = {
  columnKeys: DEFAULT_COLUMN_KEYS,
  columnWidths: {},
  sortKey: 'clientNumber',
  sortDir: 'asc',
  version: LAYOUT_VERSION,
}

/**
 * Ein gespeichertes Layout auf den aktuellen Stand heben — für den eigenen
 * Speicher **und** für den Mandanten-Standard, der auf demselben Weg kommt.
 */
export function aufAktuellenStand(parsed: Partial<ColumnState> | null): ColumnState {
  if (!parsed?.columnKeys?.length) return STANDARD_LAYOUT
  const alt = (parsed.version ?? 1) < LAYOUT_VERSION
  return {
    columnKeys:
      alt && !parsed.columnKeys.includes('clientNumber')
        ? ['clientNumber', ...parsed.columnKeys]
        : parsed.columnKeys,
    columnWidths: parsed.columnWidths ?? {},
    sortKey: parsed.sortKey ?? STANDARD_LAYOUT.sortKey,
    sortDir: parsed.sortDir ?? 'asc',
    version: LAYOUT_VERSION,
  }
}

function loadColumnState(): ColumnState {
  return aufAktuellenStand(loadUiSetting<Partial<ColumnState> | null>(COLUMN_LAYOUT_KEY, null))
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

export default function ClientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()

  const darf = useCallback(
    (permission: string) => erfuelltRecht(permission, currentUser?.permissions),
    [currentUser?.permissions],
  )
  const canManage = darf('crm.client.manage')

  // AP-30-F: **Eine** Stelle streicht, was kein Recht hat.
  const allColumns = ohneFehlendeRechte(buildColumns(t), darf)
  const filterFields = ohneFehlendeRechte(buildFilterFields(t), darf)

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(loadColumnState)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const dragKeyRef = useRef<string | null>(null)

  const [clients, setClients] = useState<Client[]>([])
  const [meta, setMeta] = useState({ total: 0, page: 1, totalPages: 1, limit: 50 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)

  // ── Internetadresse ablegen (auf die Seite, den Knopf oder den Menüpunkt) ──
  const [ablageZiel, setAblageZiel] = useState(false)
  const [knopfZiel, setKnopfZiel] = useState(false)
  const [ablage, setAblage] = useState<{ art: 'pruefen' } | { art: 'keine' } | { art: 'vorhanden'; adresse: string; kandidaten: DuplicateCandidate[] } | null>(null)
  const [startWebsite, setStartWebsite] = useState<string | null>(null)
  const anlegenMit = (adresse: string) => {
    setAblage(null)
    setStartWebsite(adresse)
    setCreateOpen(true)
  }
  /** Erst die billige Frage — steht die Webseite schon an einem Partner? —, dann der ausgefüllte Dialog. */
  const adresseAbgelegt = async (adresse: string | null) => {
    if (!canManage) return
    if (!adresse) return setAblage({ art: 'keine' })
    setAblage({ art: 'pruefen' })
    let kandidaten: DuplicateCandidate[] = []
    try {
      kandidaten = (await clientsApi.findDuplicates({ website: adresse })).data
    } catch {
      // Die Prüfung ist ein Hinweis — scheitert sie, fängt der Dialog die Dublette über Name und UID.
    }
    if (kandidaten.length) setAblage({ art: 'vorhanden', adresse, kandidaten })
    else anlegenMit(adresse)
  }
  const abgelegtRef = useRef(adresseAbgelegt)
  abgelegtRef.current = adresseAbgelegt
  // Vom Menüpunkt abgelegt: Die Adresse wartet im Speicher, bis die Seite da ist.
  const wartend = useAdressAblage((s) => s.wartend)
  useEffect(() => {
    if (!wartend) return
    const w = useAdressAblage.getState().nimm()
    if (w) void abgelegtRef.current(w.adresse)
  }, [wartend])
  const seitenFlaeche = canManage ? adressAblageflaeche(setAblageZiel, (a) => void adresseAbgelegt(a)) : {}
  const knopfFlaeche = canManage ? adressAblageflaeche(setKnopfZiel, (a) => void adresseAbgelegt(a)) : {}

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Daten laden ─────────────────────────────────────────────────────────

  const fetchData = useCallback(
    async (filters: Record<string, ActiveFilter>, currentPage: number, state: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        const params: ListClientsParams & { sortBy?: string; sortDir?: string; country?: string } = {
          page: currentPage,
          limit: 50,
          sortBy: state.sortKey,
          sortDir: state.sortDir,
        }
        for (const [key, active] of Object.entries(filters)) {
          if (!active.value) continue
          if (key === 'search') params.search = active.value
          if (key === 'fulltext') params.fulltext = active.value
          if (key === 'role') params.role = active.value as ListClientsParams['role']
          if (key === 'type') params.type = active.value as ListClientsParams['type']
          if (key === 'country') params.country = active.value.toUpperCase()
          if (key === 'isActive') params.isActive = active.value === 'true'
        }

        const { data } = await clientsApi.list(params)
        setClients(data.data)
        setMeta(data.meta)
      } catch (err) {
        setError(t('common.error'))
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    fetchData(filterValues, page, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => fetchData(newValues, 1, colState), 400)
    },
    [fetchData, colState],
  )

  // ── Spalten ─────────────────────────────────────────────────────────────

  const updateColState = useCallback((updater: (prev: ColumnState) => ColumnState) => {
    setColState((prev) => {
      const next = updater(prev)
      saveUiSetting(COLUMN_LAYOUT_KEY, next)
      return next
    })
  }, [])

  const handleSort = (key: string) => {
    if (!SERVER_SORTIERBAR.has(key)) return
    const newDir = colState.sortKey === key && colState.sortDir === 'asc' ? 'desc' : 'asc'
    const next = { ...colState, sortKey: key, sortDir: newDir as 'asc' | 'desc' }
    updateColState(() => next)
    setPage(1)
    fetchData(filterValues, 1, next)
  }

  const handleDragStart = (key: string) => {
    dragKeyRef.current = key
  }
  const handleDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault()
    setDragOverKey(key)
  }
  const handleDragEnd = () => {
    dragKeyRef.current = null
    setDragOverKey(null)
  }
  const handleDrop = (targetKey: string) => {
    const srcKey = dragKeyRef.current
    if (!srcKey || srcKey === targetKey) {
      setDragOverKey(null)
      return
    }
    updateColState((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    setDragOverKey(null)
  }

  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null)
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.getBoundingClientRect().width ?? 120
    resizeRef.current = { key, startX: e.clientX, startW }

    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = ev.clientX - resizeRef.current.startX
      const newW = Math.max(60, resizeRef.current.startW + delta)
      updateColState((prev) => ({
        ...prev,
        columnWidths: { ...prev.columnWidths, [resizeRef.current!.key]: newW },
      }))
    }
    const onUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const shownColumns = colState.columnKeys
    .map((k) => allColumns.find((c) => c.key === k))
    .filter((c): c is ColumnFieldDef => c !== undefined)

  const availableToAdd = allColumns.filter((c) => !colState.columnKeys.includes(c.key))

  // ── Zelle rendern ───────────────────────────────────────────────────────

  const renderCell = (row: Client, col: ColumnFieldDef): React.ReactNode => {
    switch (col.key) {
      case 'clientNumber':
        return <span className="font-mono text-muted-foreground">{row.clientNumber}</span>
      case 'name':
        return <span className="font-medium text-foreground">{row.name}</span>
      case 'role':
        return <RollenAbzeichen row={row} t={t} />
      case 'type':
        return t(`clients.types.${row.type}`)
      case 'supplierNumber':
        return row.supplierNumber ? <span className="font-mono">{row.supplierNumber}</span> : '—'
      case 'city':
        // Die Stadt steht an der Adresse; die Liste lädt sie nicht mit. Sie
        // bleibt als Spalte stehen, weil das Detail sie füllt — ein leeres Feld
        // ist ehrlicher als eine zweite Abfrage je Zeile.
        return '—'
      case 'email':
        return row.email ?? '—'
      case 'phone':
        return row.phone ?? '—'
      case 'projects':
        return row._count?.projects ?? 0
      case 'isActive':
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              row.isActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {row.isActive ? t('common.active') : t('common.inactive')}
          </span>
        )
      case 'vatId':
        return row.vatId ?? '—'
      case 'paymentTermsDays':
        return row.paymentTermsDays ?? '—'
      case 'supplierPaymentTermsDays':
        return row.supplierPaymentTermsDays ?? '—'
      case 'debitorNumber':
        return row.debitorNumber ?? '—'
      case 'kreditorNumber':
        return row.kreditorNumber ?? '—'
      default:
        return null
    }
  }

  const totalCols =
    shownColumns.length + (columnConfigMode && availableToAdd.length > 0 ? 1 : 0) + 1

  return (
    <div className={`${pageWide} ${ablageZiel ? 'rounded-lg outline-dashed outline-2 outline-offset-4 outline-primary' : ''}`} {...seitenFlaeche}>
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('clients.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('clients.subtitle')}</p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            // Der Name des Knopfs bleibt — ein `title` allein würde ihn für Vorlesehilfen ersetzen.
            aria-label={t('clients.create')}
            title={t('clients.websiteDrop.buttonTitle')}
            className={`inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 ${knopfZiel ? 'outline outline-2 outline-offset-2 outline-primary' : ''}`}
            {...knopfFlaeche}
          >
            + {t('clients.create')}
          </button>
        )}
      </div>

      {/* Internetadresse abgelegt: was daraus wurde */}
      {(ablageZiel || knopfZiel) && <div className="rounded-md bg-primary/10 px-4 py-2 text-sm text-foreground">{t('clients.websiteDrop.zone')}</div>}
      {ablage && (
        <div role="status" className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-4 py-2 text-sm ${ablage.art === 'pruefen' ? 'bg-muted text-muted-foreground' : 'border border-amber-500/40 bg-amber-500/10 text-foreground'}`}>
          {ablage.art === 'pruefen' && t('clients.websiteDrop.checking')}
          {ablage.art === 'keine' && <span>{t('clients.websiteDrop.notAnAddress')}</span>}
          {ablage.art === 'vorhanden' && (
            <>
              <span className="font-medium">{t('clients.websiteDrop.exists', { host: new URL(ablage.adresse).hostname.replace(/^www\./, '') })}</span>
              {ablage.kandidaten.map((k) => (
                <span key={k.id} className="inline-flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{k.clientNumber}</span>
                  {k.name}
                  <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => navigate(`/clients/${k.id}`)}>
                    {t('clients.duplicates.openExisting')}
                  </button>
                </span>
              ))}
              <button type="button" className="rounded-md border border-amber-500/60 px-3 py-1 text-xs font-medium hover:bg-amber-500/15" onClick={() => anlegenMit(ablage.adresse)}>
                {t('clients.websiteDrop.createAnyway')}
              </button>
            </>
          )}
          {ablage.art !== 'pruefen' && (
            <button type="button" className="ml-auto text-muted-foreground hover:text-foreground" aria-label={t('clients.websiteDrop.dismiss')} onClick={() => setAblage(null)}>
              ✕
            </button>
          )}
        </div>
      )}

      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="clients"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
      />

      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={t('common.configureColumns', { defaultValue: 'Spalten konfigurieren' })}
        >
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>

        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={colState}
              onReset={(value) => {
                const next = aufAktuellenStand(value as Partial<ColumnState> | null)
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColState(next)
                fetchData(filterValues, 1, next)
              }}
            />
          </div>
        )}

        {error && (
          <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Die Kundennummer ist seit dem AP-31-Nachtrag eine Spalte wie
                    jede andere — ein reiner Lieferant sucht nach seiner LF-Nummer. */}
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`relative select-none px-4 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={
                      colState.columnWidths[col.key]
                        ? { width: colState.columnWidths[col.key] }
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1">
                      {SERVER_SORTIERBAR.has(col.key) ? (
                        <button
                          type="button"
                          onClick={() => handleSort(col.key)}
                          className="flex items-center gap-1 hover:text-primary"
                        >
                          {col.label}
                          {colState.sortKey === col.key ? (
                            colState.sortDir === 'asc' ? (
                              <ArrowUp className="h-3 w-3 text-primary" />
                            ) : (
                              <ArrowDown className="h-3 w-3 text-primary" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-30" />
                          )}
                        </button>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {columnConfigMode && (
                        <button
                          type="button"
                          onClick={() =>
                            updateColState((prev) => ({
                              ...prev,
                              columnKeys: prev.columnKeys.filter((k) => k !== col.key),
                            }))
                          }
                          className="ml-1 text-muted-foreground hover:text-destructive"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                      onMouseDown={(e) => handleResizeStart(e, col.key)}
                    />
                  </th>
                ))}

                {columnConfigMode && availableToAdd.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                      defaultValue=""
                      onChange={(e) => {
                        const key = e.target.value
                        if (!key) return
                        updateColState((prev) => ({
                          ...prev,
                          columnKeys: [...prev.columnKeys, key],
                        }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ {t('common.column', { defaultValue: 'Spalte' })}</option>
                      {availableToAdd.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}

                <th className="w-4" />
              </tr>
            </thead>

            <tbody>
              {loading && clients.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}

              {!loading && clients.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {t('clients.noClients')}
                  </td>
                </tr>
              )}

              {clients.map((row, idx) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(`/clients/${row.id}`)}
                  className={`cursor-pointer border-b border-border transition-colors last:border-0 ${
                    idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  } hover:bg-accent/30`}
                >
                  {shownColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`whitespace-nowrap px-4 py-1.5 ${
                        col.align === 'right' ? 'text-right tabular-nums' : ''
                      }`}
                    >
                      {renderCell(row, col)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}

              {clients.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  {shownColumns.map((col, i) => (
                    <td key={col.key} className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                      {i === 0 &&
                        (loading ? t('common.loading') : `${meta.total} ${t('clients.count_other')}`)}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-sm text-muted-foreground">
            <span>
              {meta.page} / {meta.totalPages} ({meta.total})
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ‹
              </button>
              <button
                type="button"
                disabled={page >= meta.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-0.5 hover:bg-muted disabled:opacity-40"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      <CreateClientDialog
        open={createOpen}
        startWebsite={startWebsite}
        onClose={() => {
          setCreateOpen(false)
          setStartWebsite(null)
        }}
        onSuccess={() => fetchData(filterValues, page, colState)}
      />
    </div>
  )
}

/** Kunde, Lieferant oder beides — als Abzeichen, weil es die Identität ist. */
function RollenAbzeichen({ row, t }: { row: Client; t: Uebersetzer }) {
  const rollen: string[] = []
  if (row.isCustomer) rollen.push(t('clients.role.customer'))
  if (row.isSupplier) rollen.push(t('clients.role.supplier'))
  return (
    <span className="inline-flex gap-1">
      {rollen.map((r) => (
        <span
          key={r}
          className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
        >
          {r}
        </span>
      ))}
    </span>
  )
}
